import React, { useEffect, useRef, useState } from "react";
import CustomSwitch from "../CommonComponents/CustomSwitch/CustomSwitch.jsx";
import Close from "../../../assets/images/cancelBlack.svg";
import "./Notifications.css";
import { useDispatch, useSelector } from "react-redux";
import { useAtom } from "jotai";
import {
  globalCurrentPageTitle,
  showPayoutDetailsAtom,
} from "../../../store/globalstore.js";
import { useNavigate } from "react-router-dom";
import useApi from "../../../helpers/apiHelper/requestHelper.js";

const Notifications = () => {
  const user = useSelector((e) => e?.user?.user);
  const [, setCurrentPageTitle] = useAtom(globalCurrentPageTitle);
  const [_, setShowPayoutDetails] = useAtom(showPayoutDetailsAtom);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const api = useApi();
  const [isOpen, setIsOpen] = useState(false);
  const selectedCommunity = useSelector(
    (state) => state?.multiCommunity?.currentCommunity
  );

  const dropdownRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [isClearing, setIsClearing] = useState(false);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleOptionClick = () => setIsOpen(false);
  const toggleDropdown = () => setIsOpen((prev) => !prev);

  // Click outside to close dropdown
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Lock body scroll on mobile when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("mobile-scroll-locked");
    } else {
      document.body.classList.remove("mobile-scroll-locked");
    }
    return () => {
      document.body.classList.remove("mobile-scroll-locked");
    };
  }, [isOpen]);

  // Listen to FCM / in-app notification events
  useEffect(() => {
    const handleFcmNotification = async (e) => {
      const detail = e?.detail;
      const localTitle = detail?.title;
      const localMessage = detail?.message || detail?.body;

      // Optimistically add in-app notifications that aren't in the API yet.
      // Special alerts belong in AlertsDropdown, not the bell.
      if ((localTitle || localMessage) && !detail?.isSpecial) {
        const localItem = {
          _id: detail?._id || `local-${Date.now()}`,
          title: localTitle || "Notification",
          message: localMessage || "",
          createdAt: detail?.createdAt || new Date().toISOString(),
          isRead: false,
          isLocal: true,
        };
        setNotifications((prev) => {
          const withoutDup = prev.filter(
            (item) =>
              !(
                item.isLocal &&
                item.title === localItem.title &&
                item.message === localItem.message
              )
          );
          return [localItem, ...withoutDup];
        });
      }

      const notificationsResponse = await api(
        "get",
        "/emailNotification/byUser"
      );
      const apiItems = (notificationsResponse?.data || []).filter(
        (item) => !item?.isSpecial
      );

      setNotifications((prev) => {
        const localItems = prev.filter((item) => item.isLocal);
        const apiIds = new Set(apiItems.map((item) => item._id));
        const stillLocal = localItems.filter((item) => !apiIds.has(item._id));
        return [...stillLocal, ...apiItems];
      });
    };
    handleFcmNotification();
    window.addEventListener("fcm-notification", handleFcmNotification);
    return () => {
      window.removeEventListener("fcm-notification", handleFcmNotification);
    };
  }, [isOpen, user]);

  const formatMessage = (message) => {
    if (!message) return "";
    const stripped = message.replace(
      /<a[^>]+href=["'](https?:\/\/[^"']+)["'][^>]*>.*?<\/a>/gi,
      "$1"
    );
    return stripped.replace(
      /\s*:?\s*(?:<br\s*\/?>)?\s*(https?:\/\/[^\s<>"]+)/gi,
      (_match, url) =>
        `: <a href="${url}" target="_blank" rel="noopener noreferrer" class="viewLink">View</a>`
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const markasRead = async (id) => {
    if (String(id).startsWith("local-")) {
      setNotifications((prev) => prev.filter((item) => item._id !== id));
      return;
    }

    const response = await api("post", `/emailNotification/${id}/markAsRead`);
    if (response?.status) {
      const notificationsResponse = await api(
        "get",
        "/emailNotification/byUser"
      );
      setNotifications((prev) => {
        const localItems = prev.filter((item) => item.isLocal);
        return [
          ...localItems,
          ...(notificationsResponse?.data || []).filter(
            (item) => !item?.isSpecial
          ),
        ];
      });
    }
  };

  const clearAllNotifications = async () => {
    if (!notifications.length || isClearing) return;

    setIsClearing(true);
    try {
      const apiNotifications = notifications.filter((item) => !item.isLocal);

      if (apiNotifications.length) {
        // Mark only regular (non-special) notifications so alerts stay in AlertsDropdown
        await Promise.all(
          apiNotifications.map((item) =>
            api("post", `/emailNotification/${item._id}/markAsRead`)
          )
        );
      }

      const notificationsResponse = await api(
        "get",
        "/emailNotification/byUser"
      );
      const remaining = (notificationsResponse?.data || []).filter(
        (item) => !item?.isSpecial
      );
      setNotifications(
        remaining.length && remaining.every((item) => item.isRead)
          ? []
          : remaining.filter((item) => !item.isRead)
      );
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="notification_dropdown" ref={dropdownRef}>
      <button
        className={`dropdown_toggle ${notifications?.length > 0 && "active"} ${
          isOpen ? "active" : ""
        }`}
        onClick={toggleDropdown}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="21"
          viewBox="0 0 23.532 23.527"
          fill="none"
        >
          {/* Shared ring border — same frame path used by the profile icon (ProfileDropdown.jsx) so both header icons stay consistent */}
          <path
            className="notifIconCircle"
            d="M10.928.025C2.298.57-2.783 10.245 1.612 17.727c4.5 7.66 15.67 7.765 20.238.125C26.767 9.63 20.424-.574 10.928.025m9.525 17.1c-4.052 6.513-13.585 6.443-17.475-.178-3.903-6.643.662-14.94 8.285-15.374 8.122-.461 13.507 8.613 9.19 15.552"
            fill="currentColor"
            fillRule="evenodd"
          />
          {/* Bell scaled down + re-centered to sit inside the ring with the same inset the profile glyph uses, instead of the near-edge-to-edge 24x24 original */}
          <g transform="translate(3 3) scale(0.73)">
            <path
              className="notifIconBell"
              d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
        {notifications.filter((item) => !item?.isSpecial).length > 0 && (
          <span className="notification_badge">
            {notifications.filter((item) => !item?.isSpecial).length}
          </span>
        )} 
      </button>
      {isOpen && (
        <div className="notif_container">
          <div className="notif_header">
            <h2>Notifications</h2>
            {notifications.length > 0 && (
              <button
                type="button"
                className="notif_clearAll"
                onClick={clearAllNotifications}
                disabled={isClearing}
              >
                {isClearing ? "Clearing..." : "Clear all"}
              </button>
            )}
          </div>

          <div className="notif_list">
            {notifications.length > 0 ? (
              notifications
                .filter((item) => !item?.isSpecial)
                .map((item) => (
                <div
                  key={item._id}
                  className={`notif_card ${item.isRead && "notify_read"}`}
                >
                  <button
                    onClick={() => markasRead(item._id)}
                    className="notif_close"
                  >
                    <img
                      src={Close}
                      width="15px"
                      height="15px"
                      alt="close"
                      className="cursor-pointer cross"
                    />
                  </button>
                  <div className="notif_top">
                    {/* <h4 className="notif_title">{item.title}</h4> */}
                    <div
                      className="notif_title"
                      dangerouslySetInnerHTML={{
                        __html: item.title,
                      }}
                    />
                    <p className="notif_date">{formatDate(item.createdAt)}</p>
                  </div>
                  {item.message && (
                    <>
                      {/* <p
                        style={{ whiteSpace: "pre-line" }}
                        className="notif_message"
                      >
                        {item.message}
                      </p> */}

                      <div
                      className="notif_message"
                        dangerouslySetInnerHTML={{
                          __html: formatMessage(item.message),
                        }}
                      />
                    </>
                  )}
                </div>
              ))
            ) : (
              <p className="no-notifications">Nothing new to see here.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
